import { useCallback, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchMtrStructTree } from '#/api/imsMgmt/settings/mtrStruct/fetchTree';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import SideMsg from '#/modules/commonComponents/SideMsg';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';
import { buildMtrTemplateColumns } from '#/modules/imsMgmt/settings/mtrTemplate/data/mtrTemplateColumns';
import { getMtrTemplateApiErrorMessage } from '#/modules/imsMgmt/settings/mtrTemplate/helper/apiError';
import { mtrTemplateSideMsgBlocks } from '#/modules/imsMgmt/settings/mtrTemplate/data/mtrTemplateSideMsgConfig';
import useMtrTemplateList from '#/modules/imsMgmt/settings/mtrTemplate/hooks/useMtrTemplateList';
import { fromTree } from '#/modules/imsMgmt/settings/mtrStruct/helper/structFilterModel';
import { mtrStructTreeQueryKey } from '#/utils/queryKeys';

const MtrTemplateListPage = () => {
  const listRef = useRef(null);
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.MTR_CREATE);
  const canUpdate = useCan(PERM.MTR_UPDATE);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    removeSortAt,
    resetListQuery,
  } = useMtrTemplateList();

  const treeQuery = useQuery({
    queryKey: mtrStructTreeQueryKey(),
    queryFn: () => fetchMtrStructTree({ status: 'ACTIVE' }),
  });

  const structModel = useMemo(
    () => fromTree(treeQuery.data?.data ?? []),
    [treeQuery.data?.data],
  );

  const columns = useMemo(
    () => buildMtrTemplateColumns({
      filters: listQuery.filters || {},
      onFilterChange: setFilter,
      canShowEdit: () => canUpdate,
    }),
    [listQuery.filters, setFilter, canUpdate],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { structModel }),
    [columns, structModel],
  );

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }

    setSelectedData(null);
  }, [setSelectedData]);

  const headerAction = canCreate ? (
    <ListAddButton to={ROUTES.mtrTemplateCreate} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <h1 className="page_title shrink-0">
          <span className="border-l-5 border-primary-400 pl-2.5">物料模板</span>
        </h1>
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              tableKey="ims.mtr_template.list"
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={isError ? getMtrTemplateApiErrorMessage(error, '載入列表失敗') : undefined}
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋代號或名稱"
              headerAction={headerAction}
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={DEFAULT_LIST_SORT_FIELD_LABELS}
              onFilterChange={setFilter}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
            />
          </div>

          <SideMsg
            title="模板"
            titleValue="name"
            displayData={displayData}
            blocks={mtrTemplateSideMsgBlocks}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default MtrTemplateListPage;
